<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAdmin } from 'dashboard/composables/useAdmin';
import schedulingAPI from '../api/scheduling';

import WeeklyCalendarGrid from '../components/WeeklyCalendarGrid.vue';
import WeeklyFixedGrid from '../components/WeeklyFixedGrid.vue';
import ServicesManager from '../components/ServicesManager.vue';
import PatientsManager from '../components/PatientsManager.vue';
import ProfessionalsManager from '../components/ProfessionalsManager.vue';
import PartnersManager from '../components/PartnersManager.vue';
import ReportsView from '../components/ReportsView.vue';
import AppointmentModal from '../components/AppointmentModal.vue';
import EnrollmentModal from '../components/EnrollmentModal.vue';
import EnrollmentDetailsModal from '../components/EnrollmentDetailsModal.vue';
import SchedulingSettingsModal from '../components/SchedulingSettingsModal.vue';

const route = useRoute();
const router = useRouter();
const { isAdmin } = useAdmin();

const allTabs = [
  { id: 'agenda', label: 'Agenda', icon: 'calendar', adminOnly: false },
  { id: 'grade_fixa', label: 'Grade de Vagas', icon: 'grid', adminOnly: false },
  { id: 'clientes', label: 'Pacientes', icon: 'users', adminOnly: false },
  { id: 'profissionais', label: 'Profissionais', icon: 'user-check', adminOnly: false },
  { id: 'servicos', label: 'Serviços', icon: 'clipboard-list', adminOnly: false },
  { id: 'parceiros', label: 'Parceiros', icon: 'shield', adminOnly: true },
  { id: 'relatorios', label: 'Relatórios', icon: 'bar-chart', adminOnly: true },
];

const tabs = computed(() => {
  if (isAdmin.value) return allTabs;
  return allTabs.filter(t => !t.adminOnly);
});

const validateTabAccess = tabId => {
  const tab = allTabs.find(t => t.id === tabId);
  if (tab && tab.adminOnly && !isAdmin.value) {
    return 'agenda';
  }
  return tabId || 'agenda';
};

const activeTab = ref(validateTabAccess(route.params.tab));

const tabComponentMap = {
  agenda: WeeklyCalendarGrid,
  grade_fixa: WeeklyFixedGrid,
  clientes: PatientsManager,
  profissionais: ProfessionalsManager,
  servicos: ServicesManager,
  parceiros: PartnersManager,
  relatorios: ReportsView,
};

const currentTabComponent = computed(() => {
  return tabComponentMap[activeTab.value] || WeeklyCalendarGrid;
});

const services = ref([]);
const professionals = ref([]);
const patients = ref([]);
const partners = ref([]);

// Modals State
const showAppointmentModal = ref(false);
const showEnrollmentModal = ref(false);
const showEnrollmentDetailsModal = ref(false);
const showSettingsModal = ref(false);

const selectedEnrollmentId = ref(null);
const enrollmentInitialData = ref(null);
const editingAppointment = ref(null);
const appointmentInitialData = ref(null);

const loadSharedData = async () => {
  try {
    const [srvRes, profRes, patRes, partRes] = await Promise.all([
      schedulingAPI.getServices(),
      schedulingAPI.getProfessionals(),
      schedulingAPI.getPatients(),
      schedulingAPI.getPartners(),
    ]);
    services.value = srvRes.data || [];
    professionals.value = profRes.data || [];
    patients.value = patRes.data || [];
    partners.value = partRes.data || [];
  } catch (err) {
    console.error('Erro ao carregar dados compartilhados do agendamento:', err);
  }
};

const gridKey = ref(0);

const switchTab = tabId => {
  const safeTabId = validateTabAccess(tabId);
  if (activeTab.value === safeTabId) return;
  activeTab.value = safeTabId;
  const accountId = route.params.accountId;
  router.replace({ name: 'scheduling_dashboard_tab', params: { accountId, tab: safeTabId } }).catch(() => {});
};

watch(
  () => route.params.tab,
  newTab => {
    if (newTab) {
      const safeTabId = validateTabAccess(newTab);
      if (activeTab.value !== safeTabId) {
        activeTab.value = safeTabId;
      }
    }
  }
);

// Rascunhos Temporários (Draft Cache) para não perder dados ao fechar o modal
const appointmentDraft = ref(null);
const enrollmentDraft = ref(null);

const onOpenEnrollmentModal = data => {
  loadSharedData();
  enrollmentInitialData.value = {
    ...(enrollmentDraft.value || {}),
    ...(data || {}),
  };
  showEnrollmentModal.value = true;
};

const onCloseEnrollmentModal = draftData => {
  showEnrollmentModal.value = false;
  if (draftData && typeof draftData === 'object') {
    enrollmentDraft.value = {
      ...enrollmentInitialData.value,
      ...draftData,
    };
  }
};

const selectedEnrollmentInfo = ref(null);

const onOpenEnrollmentDetails = info => {
  if (typeof info === 'object' && info !== null) {
    selectedEnrollmentId.value = info.enrollmentId;
    selectedEnrollmentInfo.value = info;
  } else {
    selectedEnrollmentId.value = info;
    selectedEnrollmentInfo.value = { enrollmentId: info };
  }
  showEnrollmentDetailsModal.value = true;
};

const onEnrollmentSaved = () => {
  enrollmentDraft.value = null; // Limpa rascunho após salvar com sucesso
  gridKey.value += 1;
  loadSharedData();
};

const onOpenAppointmentModal = data => {
  loadSharedData();
  if (data && data.is_enrollment && data.enrollment_id) {
    onOpenEnrollmentDetails({
      enrollmentId: data.enrollment_id,
      slotDate: data.start_time ? data.start_time.split('T')[0] : null,
      timeSlot: data.start_time ? data.start_time.split('T')[1].substring(0, 5) : null,
    });
    return;
  }
  if (data && data.id) {
    editingAppointment.value = data;
    appointmentInitialData.value = null;
  } else {
    editingAppointment.value = null;
    appointmentInitialData.value = {
      ...(appointmentDraft.value || {}),
      ...(data || {}),
    };
  }
  showAppointmentModal.value = true;
};

const onCloseAppointmentModal = draftData => {
  showAppointmentModal.value = false;
  if (!editingAppointment.value && draftData && typeof draftData === 'object') {
    appointmentDraft.value = {
      ...appointmentInitialData.value,
      ...draftData,
    };
  }
};

const onAppointmentSaved = () => {
  appointmentDraft.value = null; // Limpa rascunho após salvar com sucesso
  gridKey.value += 1;
  loadSharedData();
};

onMounted(() => {
  loadSharedData();
});
</script>

<template>
  <div class="flex-1 flex flex-col h-full overflow-hidden bg-n-background text-n-slate-12 select-none">
    <!-- Top Header Bar (Chatwoot v4 / Linear Standard) -->
    <header class="px-6 py-3 bg-n-surface-1 border-b border-n-weak flex flex-col gap-2.5">
      <div class="flex items-center justify-between">
        <!-- Title & Subtitle -->
        <div class="flex items-center gap-3">
          <div>
            <h1 class="text-lg font-semibold text-n-slate-12 leading-tight flex items-center gap-2">
              Atendimentos
            </h1>
            <p class="text-xs text-n-slate-11">
              Gestão de agenda clínica, grade de turmas e prontuários.
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <!-- Quick Public Link Button -->
          <button
            type="button"
            class="h-8 px-2.5 rounded-lg text-xs font-medium text-n-slate-11 hover:text-n-slate-12 bg-n-alpha-1 hover:bg-n-alpha-2 border border-n-weak transition-colors flex items-center gap-1.5 cursor-pointer shadow-xs"
            title="Ver e copiar link público de agendamento"
            @click="showSettingsModal = true"
          >
            <svg class="w-3.5 h-3.5 text-n-slate-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <span>Link Público</span>
          </button>

          <!-- Settings Button (Admin Only) -->
          <button
            v-if="isAdmin"
            type="button"
            class="h-8 p-2 rounded-lg text-n-slate-11 hover:text-n-slate-12 bg-n-alpha-1 hover:bg-n-alpha-2 border border-n-weak transition-colors cursor-pointer shadow-xs"
            title="Configurações e Integrações"
            @click="showSettingsModal = true"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Segmented TabBar (Chatwoot v4 TabBar Pattern) -->
      <nav class="flex items-center p-0.5 rounded-lg bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak/60 w-fit max-w-full overflow-x-auto gap-0.5">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="px-3 py-1 rounded-md text-xs font-medium transition-all duration-200 flex items-center gap-1.5 cursor-pointer shrink-0"
          :class="
            activeTab === tab.id
              ? 'bg-n-solid-active shadow-xs text-n-slate-12 font-semibold'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2/50'
          "
          @click="switchTab(tab.id)"
        >
          <!-- Agenda Icon -->
          <svg v-if="tab.id === 'agenda'" class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <!-- Grade de Vagas Icon -->
          <svg v-else-if="tab.id === 'grade_fixa'" class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
          <!-- Clientes Icon -->
          <svg v-else-if="tab.id === 'clientes'" class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          <!-- Profissionais Icon -->
          <svg v-else-if="tab.id === 'profissionais'" class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <!-- Servicos Icon -->
          <svg v-else-if="tab.id === 'servicos'" class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <!-- Parceiros Icon -->
          <svg v-else-if="tab.id === 'parceiros'" class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <!-- Relatorios Icon -->
          <svg v-else-if="tab.id === 'relatorios'" class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <span>{{ tab.label }}</span>
        </button>
      </nav>
    </header>

    <!-- Main Content Area with KeepAlive for Instant Tab Switching (0ms) -->
    <main class="flex-1 overflow-y-auto p-5 bg-n-background">
      <KeepAlive>
        <component
          :is="currentTabComponent"
          :key="activeTab === 'agenda' || activeTab === 'grade_fixa' ? `${activeTab}-${gridKey}` : activeTab"
          :services="services"
          :professionals="professionals"
          :initial-patients="patients"
          :initial-professionals="professionals"
          :initial-services="services"
          :initial-partners="partners"
          @open-appointment-modal="onOpenAppointmentModal"
          @open-enrollment-modal="onOpenEnrollmentModal"
          @open-enrollment-details="onOpenEnrollmentDetails"
          @saved="loadSharedData"
        />
      </KeepAlive>
    </main>

    <!-- Modal 1: Appointment Modal (Agenda Tradicional) -->
    <AppointmentModal
      v-if="showAppointmentModal"
      :appointment="editingAppointment"
      :initial-data="appointmentInitialData"
      :services="services"
      :professionals="professionals"
      :patients="patients"
      :partners="partners"
      @close="onCloseAppointmentModal"
      @saved="onAppointmentSaved"
    />

    <!-- Modal 2: Enrollment Modal (Matrícula na Grade Fixa) -->
    <EnrollmentModal
      v-if="showEnrollmentModal"
      :initial-data="enrollmentInitialData"
      :services="services"
      :professionals="professionals"
      :patients="patients"
      @close="onCloseEnrollmentModal"
      @saved="onEnrollmentSaved"
    />

    <!-- Modal 3: Enrollment Details & Attendance & Rescheduling Modal -->
    <EnrollmentDetailsModal
      v-if="showEnrollmentDetailsModal"
      :enrollment-id="selectedEnrollmentId"
      :enrollment-info="selectedEnrollmentInfo"
      :services="services"
      :professionals="professionals"
      @close="showEnrollmentDetailsModal = false"
      @updated="onEnrollmentSaved"
    />

    <!-- Modal 4: Settings & Integrations Modal -->
    <SchedulingSettingsModal
      v-if="showSettingsModal"
      @close="showSettingsModal = false"
    />
  </div>
</template>
